import { useDisclosure } from "@mantine/hooks";
import DOMPurify from "dompurify";
import { IoIosSend, IoMdCheckmark } from "react-icons/io";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import { BiError } from "react-icons/bi";
import { channelColors, channelIcons } from "@/constants/channelIcons";
import { api } from "@/lib/api-client";
import { LimeTimeline } from "@/Components/LimeTimeline";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Snippet,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { getLocalTimeZone } from "@internationalized/date";
import { i18n } from "@lingui/core";
import { Mail, MailOpen, MailX } from "lucide-react";
import {
  getNotificationEventGroups,
  matchesEvent,
  type NotificationLog,
} from "./appointmentNotificationRows";
import { shouldRenderNotificationAsHtml } from "./notificationContent";

export type { NotificationLog };

type EmailActivityEventType =
  | "stored"
  | "complained"
  | "unsubscribed"
  | "clicked"
  | "opened"
  | "failed"
  | "delivered"
  | "rejected"
  | "accepted";

export const statusIcons = (
  deliveryStatus: NotificationLog["deliveryStatus"],
  type: NotificationLog["originalType"],
) => {
  const color = channelColors[type];

  if (deliveryStatus === "pending") {
    return <IoIosSend size={20} color={"gray"} />;
  } else if (deliveryStatus === "error") {
    return <BiError size={20} color={"red"} />;
  } else if (deliveryStatus === "delivered" && type !== "sms") {
    return <IoMdCheckmark size={20} color={"gray"} />;
  } else if (deliveryStatus === "delivered" && type === "sms") {
    return <IoCheckmarkDoneOutline size={20} color={"gray"} />;
  } else if (deliveryStatus === "seen") {
    return <IoCheckmarkDoneOutline size={20} color={color} />;
  }
};

const emailStatusIcons = (eventType: EmailActivityEventType) => {
  switch (eventType) {
    case "opened":
      return <MailOpen size={20} color={"gray"} />;
    case "delivered":
      return <Mail size={20} color={"gray"} />;
    case "accepted":
      return <IoIosSend size={20} color={"gray"} />;
    case "failed":
      return <MailX size={20} color={"gray"} />;
    default:
      return <IoIosSend size={20} color={"gray"} />;
  }
};

// A notification that fails before it ever reaches Infobip is logged with an
// empty body and no `requestId`. The reason lives in the machine-readable
// `status_message` code (see fallback-flow-service.ts). Translate the known
// codes into a human-readable explanation so the detail modal explains the
// blank message instead of showing an empty content box with no message id.
// Returns null when the row sent normally (has content) or carries no
// recognizable failure reason.
export function getNotificationFailureExplanation(log: {
  status: string;
  status_message: string | null;
  message_content: string | null;
  requestId: string | null;
}): string | null {
  const statusMessage = log.status_message ?? "";
  const hasContent = !!(log.message_content ?? "").trim();

  if (statusMessage === "NO_DELIVERABLE_CHANNEL") {
    return t`Sporočilo ni bilo poslano, ker stranka nima telefonske številke oziroma e-poštnega naslova za noben nastavljen kanal obvestil.`;
  }
  if (statusMessage === "MONTHLY_LIMIT_EXCEEDED") {
    return t`Sporočilo ni bilo poslano, ker je bila presežena mesečna omejitev števila sporočil.`;
  }
  if (statusMessage.startsWith("COUNTRY_RESTRICTED")) {
    return t`Sporočilo ni bilo poslano, ker je pošiljanje sporočil v državo stranke onemogočeno.`;
  }
  if (statusMessage === "INVALID_NUMBER OR CONTENT") {
    return t`Sporočilo ni bilo poslano, ker je telefonska številka oziroma vsebina sporočila neveljavna.`;
  }

  // Any other terminal failure that produced no body and no Infobip message id:
  // explain generically rather than rendering an empty content box.
  const isTerminalError = log.status === "error" || log.status === "failed";
  if (isTerminalError && !hasContent && !log.requestId) {
    return t`Sporočilo ni bilo poslano, zato nima vsebine. Preverite nastavitve obvestil in kontaktne podatke stranke.`;
  }

  return null;
}

export function NotificationLogSections({
  notificationLog,
}: {
  notificationLog: NotificationLog[];
}) {
  const groups = getNotificationEventGroups();

  const groupedEvents = groups.flatMap((group) => group.events);

  return (
    <>
      {groups.map((group) => (
        <NotificationLogSection
          key={group.label}
          label={group.label}
          notifications={notificationLog.filter((nl) =>
            matchesEvent(nl, group.events),
          )}
        />
      ))}

      <NotificationLogSection
        label={t`Ostalo`}
        notifications={notificationLog.filter(
          (nl) => !matchesEvent(nl, groupedEvents),
        )}
      />
    </>
  );
}

function NotificationLogSection({
  label,
  notifications,
}: {
  label: string;
  notifications: NotificationLog[];
}) {
  if (!notifications.length) {
    return null;
  }

  return (
    <>
      <p className="text-sm leading-5 font-medium">{label}</p>
      {notifications.map((nl) => (
        <NotificationPreviewRow key={nl.notificationLogId} notification={nl} />
      ))}
    </>
  );
}

const NotificationPreviewRow = ({
  notification,
}: {
  notification: NotificationLog;
}) => {
  const [opened, { open, close }] = useDisclosure(false);

  const notificationType =
    notification.notificationType || notification.originalType;

  return (
    <>
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between p-1 hover:bg-gray-100"
        onClick={open}
      >
        <p aria-hidden="true" className="text-sm leading-5">
          {channelIcons[notificationType]}
        </p>

        <div className="flex items-center gap-2">
          <p className="text-sm leading-5">{notification.timeSent}</p>
          <div>
            {statusIcons(notification.deliveryStatus, notificationType)}
          </div>
        </div>
      </button>

      <NotificationLogDetailModal
        isOpen={opened}
        onClose={close}
        title={`${notificationType} - ${notification.timeSent}`}
        notificationLogId={notification.notificationLogId}
      />
    </>
  );
};

export const NotificationLogDetailModal = ({
  isOpen,
  onClose,
  title,
  notificationLogId,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  notificationLogId: number;
}) => (
  <LimeModal size="xl" isOpen={isOpen} onClose={onClose} title={title}>
    <ModalContent>
      <ModalHeader>
        <Trans>Podrobnosti obvestila</Trans>
      </ModalHeader>
      <ModalBody>
        <NotificationActivityPreview notificationLogId={notificationLogId} />
      </ModalBody>
      <ModalFooter></ModalFooter>
    </ModalContent>
  </LimeModal>
);

const NotificationActivityPreview = ({
  notificationLogId,
}: {
  notificationLogId: number;
}) => {
  const { data } =
    api.notification.useGetNotificationLogDetails(notificationLogId);

  if (!data) {
    return null;
  }

  const startContent = (() => {
    const hasContent = !!(data.notificationLog.message_content ?? "").trim();
    const failureExplanation = getNotificationFailureExplanation(
      data.notificationLog,
    );

    const jsxContent = (() => {
      const content = data.notificationLog.message_content || "";
      if (
        shouldRenderNotificationAsHtml(
          data.notificationLog.notificationType,
          content,
        )
      ) {
        const sanitizedContent = DOMPurify.sanitize(content);
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          ></div>
        );
      } else {
        return <p className="text-black">{content}</p>;
      }
    })();

    const deliveryEvent = (() => {
      return (
        data.notificationLog.fallbackNotificationActivity.find((activity) =>
          ["delivered", "seen"].includes(activity.deliveryStatus),
        ) ||
        data.notificationLog.emailNotificationActivity.find((activity) =>
          ["delivered", "opened"].includes(activity.eventType),
        )
      );
    })();

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-2">
          <div className="flex justify-between">
            <span className="text-foreground-500 text-sm">
              <Trans>Poslano ob:</Trans>
            </span>
            <span className="text-sm font-medium">
              {new Date(data.notificationLog.timeSent).toLocaleString(
                i18n.locale,
                {
                  timeZone: getLocalTimeZone(),
                },
              )}
            </span>
          </div>

          {deliveryEvent ? (
            <div className="flex w-full items-center justify-between">
              <span className="text-foreground-500 text-sm">
                <Trans>Dostavljeno ob:</Trans>
              </span>
              <span className="text-sm font-medium">
                {new Date(deliveryEvent.timestamp).toLocaleString(i18n.locale, {
                  timeZone: getLocalTimeZone(),
                })}
              </span>
            </div>
          ) : null}

          {!hasContent && failureExplanation ? (
            <div className="mt-3">
              <LimeAlert color="warning" message={failureExplanation} />
            </div>
          ) : (
            <div className="mt-3">
              <span className="text-foreground-500 mb-2 block text-sm">
                <Trans>Vsebina:</Trans>
              </span>
              <div className="rounded-medium border-default-200 bg-content2 min-h-[150px] overflow-auto border p-4">
                {jsxContent}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  })();

  return (
    <div className="flex flex-col gap-4">
      {startContent}
      <LimeTimeline
        highlightFirst={false}
        items={data.notificationLog.fallbackNotificationActivity.map(
          (activity) => ({
            title: (
              <div className="flex items-center justify-between gap-2">
                <span className="capitalize">{activity.channel}</span>
                <div className="flex items-center gap-2 capitalize">
                  {statusIcons(activity.deliveryStatus, activity.channel)}
                  {activity.deliveryStatusLocalized}
                </div>
              </div>
            ),
            description: (
              <div className="flex flex-col text-sm">
                <span className="text-foreground-500">
                  {activity.statusDescription}
                </span>
                <span className="text-foreground-500">
                  {activity.errorDescription}
                </span>
              </div>
            ),
            timestamp: new Date(activity.timestamp).toLocaleString(
              i18n.locale,
              {
                timeZone: getLocalTimeZone(),
              },
            ),
            icon: channelIcons[activity.channel],
          }),
        )}
      />

      <LimeTimeline
        highlightFirst={false}
        items={data.notificationLog.emailNotificationActivity.map(
          (activity) => ({
            title: (
              <div className="flex items-center justify-between gap-2">
                <span className="capitalize">Email</span>
                <div className="flex items-center gap-2 capitalize">
                  {emailStatusIcons(activity.eventType)}
                  {activity.eventType}
                </div>
              </div>
            ),
            description: activity.errorMessage ? (
              <span className="text-foreground-500">
                {activity.errorMessage}
              </span>
            ) : (
              ""
            ),
            timestamp: new Date(activity.timestamp).toLocaleString(
              i18n.locale,
              {
                timeZone: getLocalTimeZone(),
              },
            ),
            icon: channelIcons["email"],
          }),
        )}
      />

      {data.notificationLog.requestId ? (
        <span className="mt-4 text-xs text-gray-500">
          <Trans>ID obvestila:</Trans>
          <Snippet hideSymbol size="sm">
            {data.notificationLog.requestId}
          </Snippet>
        </span>
      ) : null}
    </div>
  );
};
